import { PixelRatio } from 'react-native';

import type { MenuPickerContext } from '../state/menu-picker';
import type {
  MenuItemCustomizationOption,
  MenuItemCustomizationOptionGroup,
  MenuPickerAspectOption,
} from '../types';

export { getOffersAndRewardsHref } from './get-offers-and-rewards-href';

const BASE_MENU_URL = '/menu';

export const CHIPS_MENU_PICKER_SLUG = 'chips';

/**
 * Chips-only visibility filter for picker aspect options.
 *
 * Canonical rule: `enrichMenuPicker` in the menu BFF hides unavailable chip flavors
 * before the response is sent. This helper mirrors that behavior as a client-side
 * safety net (e.g. stale cache or pre-deploy API responses) and must stay aligned
 * with the BFF chips slug check.
 */
export function getVisiblePickerAspectOptions(
  slug: string,
  options: MenuPickerAspectOption[] | undefined
): MenuPickerAspectOption[] {
  if (!options?.length) {
    return [];
  }

  if (slug !== CHIPS_MENU_PICKER_SLUG) {
    return options;
  }

  return options.filter(option => option.item?.isAvailable);
}

export const getUrlForItemDetailPageFromSlug = (slug: string) => {
  return `${BASE_MENU_URL}/${slug}`;
};

export const formatCentsToDollars = (cents: number) => {
  // Always include decimal places
  return cents > 0 ? `$${(cents / 100).toFixed(2)}` : null;
};

export const formatCentsToDollarsWithPlusSign = (cents: number) => {
  return cents > 0 ? `+ ${formatCentsToDollars(cents)}` : null;
};

/** Customize UI / VoiceOver: explicit upcharge wording before the dollar amount. */
export function formatCustomizationAdditionalCostLabel(cents: number): string | null {
  if (cents <= 0) {
    return null;
  }
  const dollars = formatCentsToDollars(cents);
  return dollars ? `Additional cost, ${dollars}` : null;
}

/**
 * Description + formatted base price for size/aspect rows.
 * CMS copy often already includes the price; avoid appending POS price again (e.g. "$6.79 • $6.79").
 */
export function pickerAspectOptionSubtitle(opt: {
  description?: string | null;
  item?: { basePriceCents?: number | null } | null;
}): string | undefined {
  const desc = opt.description?.trim() ?? '';
  const cents = opt.item?.basePriceCents;

  let priceStr: string | undefined;
  if (cents != null) {
    if (cents > 0) {
      priceStr = formatCentsToDollars(cents) ?? undefined;
    } else {
      priceStr = '$0.00';
    }
  }

  if (!desc && !priceStr) {
    return undefined;
  }
  if (!priceStr) {
    return desc || undefined;
  }
  if (!desc) {
    return priceStr;
  }
  if (desc === priceStr || desc.includes(priceStr)) {
    return desc;
  }

  return `${desc} • ${priceStr}`;
}

export const formatCaloriesOffsetText = (caloriesOffset?: number): string => {
  // We still want to display this information when calories are 0.
  return caloriesOffset !== null || caloriesOffset !== undefined ? `${caloriesOffset} Cal` : '';
};

const dpr = Math.max(PixelRatio.get(), 3).toString();

export function optimizeSanityImageUri(uri: string = '', imageHeight: number): string {
  const imageUri = new URL(uri);
  imageUri.searchParams.set('h', imageHeight.toString());
  imageUri.searchParams.set('dpr', dpr);
  imageUri.searchParams.set('fm', 'webp');

  return imageUri.toString();
}

export function forEachCustomizationOptionInPickerAspectOption(
  pickerAspectOption: MenuPickerContext['selectedPickerAspectOption'],
  cb: ({
    customizationOption,
    customizationOptionGroup,
  }: {
    customizationOption: MenuItemCustomizationOption;
    customizationOptionGroup: MenuItemCustomizationOptionGroup;
  }) => void
) {
  for (const customizationOptionGroup of pickerAspectOption?.item?.customizations?.displayGroups ??
    []) {
    for (const customizationOption of customizationOptionGroup?.options ?? []) {
      cb({ customizationOption, customizationOptionGroup });
    }
  }
}
